"use client";

/**
 * React Query client singleton for the web app.
 *
 * One client per browser session — created lazily so SSR doesn't share
 * the same instance across requests. Wrapped in <QueryClientProvider />
 * inside AppProviders.
 *
 * Defaults:
 * - `staleTime: 30s`. Data is considered fresh for half a minute, so
 *   remounts within that window skip the network. Inside that window
 *   the cache acts like the old module-level filesCache used to.
 * - `refetchOnWindowFocus: false`. Returning to a backgrounded tab must NOT
 *   trigger a burst of refetches — that read as instability ("flaky on
 *   refocus"). The two useQuery consumers (files, telegram) stay fresh via
 *   socket invalidation / polling, so focus refetch bought churn, not freshness.
 * - `retry: 1`. Single retry on transient network blips, no aggressive
 *   storm on real failures.
 *
 * Server data (file listings, etc.) goes through this. UI state stays
 * in Zustand. Mutations call `queryClient.setQueryData()` for
 * optimistic updates; React Query handles rollback on error via the
 * `onError` + previous-data snapshot pattern in `onMutate`.
 */

import { QueryClient } from "@tanstack/react-query";

let _client: QueryClient | null = null;

export function getQueryClient(): QueryClient {
  if (_client) return _client;
  _client = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: 1,
      },
      mutations: {
        retry: 0,
      },
    },
  });
  return _client;
}
